import logo from "../../assets/image/logo.svg";
import { useContext, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ConfigContext from "../../context/config/ConfigContext";
import { useLocation, useNavigate } from "react-router-dom";
import Category from "../../components/icons/Category";
import ScrollableCategoryBar from "../../components/scrollable/ScrollableCategoryBar";
import SkeletonCategorie from "../../components/skeleton/SkeletonCategorie";

import { useModal } from "../../context/ModalContext";
import MenuLandingGrid from "../../components/grid/MenuLandingGrid";
import MenuProductList from "../../components/menu/MenuProductList";
import { useMenuEffects } from "../../hooks/useMenuEffect";

function Menu() {
    const navigate = useNavigate();
    const location = useLocation();
    const { preselectedCategoryId } = location.state || {};

    const {
        categories,
        selectedCategory,
        setSelectedCategory,
        products,
        strings,
        isLoadingProducts,
        weeklyNewProduct
    } = useContext(ConfigContext);

    const { openModal, hasSeenModal } = useModal();
    const categoryScrollRef = useRef(null);
    const triggerRef = useRef(null);

    const [hasActiveCategory, setHasActiveCategory] = useState(() => {
        if (preselectedCategoryId) return true;
        return !!sessionStorage.getItem("activeCategory");
    });

    const { showScrollTop, isSticky, isLoadingCategories } = useMenuEffects({
        categories, selectedCategory, setSelectedCategory, isLoadingProducts,
        preselectedCategoryId, categoryScrollRef, triggerRef, setHasActiveCategory,
        hasSeenModal, weeklyNewProduct, openModal, navigate
    });

    // 🚀 CENTRATURA ORIZZONTALE AD ALTA AFFIDABILITÀ CON POLLING GEOMETRICO
    useEffect(() => {
        if (isLoadingCategories || !selectedCategory) return;

        // Teniamo traccia del numero di tentativi e dell'ultimo scroll effettuato
        let attempts = 0;
        let intervalId = null;
        let lastTargetLeft = -1;

        const centerActiveCategory = () => {
            attempts++;

            // 1. Recupero dei nodi del DOM (Piano A col Ref, Piano B diretto)
            const container = categoryScrollRef.current || document.querySelector('[data-category-bar="true"]');
            if (!container) return;

            const activeWrapper = container.querySelector('[data-active="true"]');
            if (!activeWrapper) return;

            // 2. Lettura delle geometrie reali
            const containerWidth = container.offsetWidth;
            const elementLeft = activeWrapper.offsetLeft;
            const elementWidth = activeWrapper.offsetWidth;

            // Se il container o l'elemento non sono ancora renderizzati fisicamente (dimensioni a 0), saltiamo il turno
            if (containerWidth === 0 || elementWidth === 0) return;

            // 3. Calcolo del punto centrale esatto
            const targetScrollLeft = elementLeft - containerWidth / 2 + elementWidth / 2;

            // 🌟 TRUCCO DA PRO: Se la posizione calcolata è identica a quella del tentativo precedente, 
            // significa che il layout si è stabilizzato. Possiamo pulire l'intervallo e fermarci!
            if (targetScrollLeft === lastTargetLeft && attempts > 3) {
                clearInterval(intervalId);
                return;
            }

            lastTargetLeft = targetScrollLeft;

            // 4. Esecuzione dello scroll fluido
            container.scrollTo({
                left: targetScrollLeft,
                behavior: attempts <= 2 ? "auto" : "smooth" // Il primissimo colpo è istantaneo per evitare sfarfallii, i successivi sono fluidi
            });

            // Sicurezza: dopo 15 tentativi (circa 750ms) fermiamo comunque il ciclo per non pesare sulla CPU
            if (attempts > 15) {
                clearInterval(intervalId);
            }
        };

        // Fari partire un micro-intervallo ogni 50ms per inseguire il rendering hardware del telefono
        intervalId = setInterval(centerActiveCategory, 50);

        // Pulizia immediata se lo stato cambia improvvisamente o il componente si smonta
        return () => {
            if (intervalId) clearInterval(intervalId);
        };

    }, [selectedCategory, hasActiveCategory, isLoadingCategories]);

    const selectCategoryAction = (id) => {
        setSelectedCategory(id);
        setHasActiveCategory(true);
        sessionStorage.setItem("activeCategory", id);
        window.scrollTo(0, 0);
    };

    return (
        <main className="min-h-safe-screen pb-16 bg-[#090D16] text-secondary selection:bg-primary/20">
            <div className="flex justify-center py-4 bg-[#0D121F] border-b border-accent/30 shadow-md">
                <img
                    onClick={() => {
                        sessionStorage.clear();
                        setSelectedCategory(null);
                        setHasActiveCategory(false);
                        navigate("/");
                    }}
                    src={logo}
                    className="h-14 active:scale-95 transition-transform duration-200 cursor-pointer"
                    alt="logo"
                />
            </div>

            <AnimatePresence mode="wait">
                {!hasActiveCategory ? (
                    <motion.div key="grid-view" exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
                        <MenuLandingGrid
                            categories={categories}
                            isLoading={isLoadingCategories}
                            onCategorySelect={selectCategoryAction}
                        />
                    </motion.div>
                ) : (
                    <motion.div key="menu-active" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
                        <div ref={triggerRef} className="h-1" />

                        <motion.div className={`z-30 transition-all duration-300 ${isSticky ? "fixed top-0 left-0 right-0 border-b bg-[#090D16]/85 backdrop-blur-md border-accent/40 shadow-xl px-4 pt-4 pb-3" : "px-4 pt-4 pb-3 border-b border-transparent"}`}>
                            {isLoadingCategories ? <SkeletonCategorie /> : (
                                <div className="max-w-md mx-auto">
                                    {/* 🌟 Aggiunto data-category-bar="true" come paracadute se l'innerRef fallisce l'inoltro */}
                                    <div data-category-bar="true" className="w-full">
                                        <ScrollableCategoryBar innerRef={categoryScrollRef}>
                                            {categories?.map((item, idx) => (
                                                <div
                                                    key={idx}
                                                    data-active={selectedCategory === item.id ? "true" : "false"}
                                                    className="inline-block shrink-0"
                                                >
                                                    <Category
                                                        name={item.name}
                                                        icon={item.icon}
                                                        isSelected={selectedCategory === item.id}
                                                        size="md"
                                                        onClick={() => selectCategoryAction(item.id)}
                                                    />
                                                </div>
                                            ))}
                                        </ScrollableCategoryBar>
                                    </div>
                                </div>
                            )}
                        </motion.div>

                        {isSticky && <div className="h-[84px]" />}

                        <MenuProductList
                            products={products} selectedCategory={selectedCategory} isLoadingProducts={isLoadingProducts}
                            strings={strings} isSticky={isSticky} showScrollTop={showScrollTop}
                            categoryScrollRef={categoryScrollRef} navigate={navigate}
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </main>
    );
}

export default Menu;